'use client'

import React, { useState, useCallback, useMemo, useTransition } from 'react'
import { Card } from '@/ui/components/Card'
import { CardType } from '@/ui/components/Card/types'
import type { Event, Tag, Media as MediaType, EventType as EventTypeData } from '@/payload-types'
import { Title } from '@/ui/components/Title'
import { Button } from '@/ui/atoms/Button'
import { TypedLocale } from 'payload'
import { FilterRow } from '@/ui/components/FilterRow'
import { sendGAEvent } from '@next/third-parties/google'
import { trackEvent } from '@/lib/analytics/posthog'

type AuthorData = {
  name: string
  image?: MediaType | string | null
  verified?: boolean
}

interface EventsOverviewClientProps {
  initialEvents: Event[]
  eventTypes: EventTypeData[]
  title?: string
  filterTitle?: string
  showTitle?: boolean
  emptyMessage?: string
  locale?: TypedLocale
}

export function EventsOverviewClient({
  initialEvents,
  eventTypes,
  title = 'Upcoming Events',
  filterTitle = 'Filter Event Types',
  showTitle = true,
  emptyMessage = 'No events found.',
  locale,
}: EventsOverviewClientProps) {
  const [showAll, setShowAll] = useState(false)
  const [activeFilter, setActiveFilter] = useState<string>('all')
  const [isPending, startTransition] = useTransition()

  // Create filter options
  const filterOptions = useMemo(() => {
    const options: { label: string; value: string }[] = []

    eventTypes.forEach((type) => {
      options.push({
        label: type.title,
        value: type.id,
      })
    })

    return options
  }, [eventTypes])

  // Filter events based on active filter
  const filteredEvents = useMemo(() => {
    if (activeFilter === 'all') {
      return initialEvents
    }

    return initialEvents.filter((event) => {
      const eventType = event.eventType as EventTypeData | string | undefined
      const eventTypeId = typeof eventType === 'object' ? eventType.id : eventType
      return eventTypeId === activeFilter
    })
  }, [initialEvents, activeFilter])

  const eventsPerPage = 9
  const displayedEvents = showAll ? filteredEvents : filteredEvents.slice(0, eventsPerPage)
  const remainingEvents = filteredEvents.length - eventsPerPage

  const handleFilterChange = useCallback((filterValue: string) => {
    startTransition(() => {
      setActiveFilter(filterValue)
      setShowAll(false) // Reset pagination when filter changes

      // Send analytics event
      sendGAEvent('event', 'eventFilterChanged', {
        value: filterValue,
      })
      trackEvent('eventFilterChanged', { value: filterValue })
    })
  }, [])

  return (
    <div className="space-y-8">
      {showTitle && (
        <Title hierarchy="h3" className="lg:mb-section mb-mobile-section">
          {title}
        </Title>
      )}

      {/* Filter Row */}
      {filterOptions.length > 1 && (
        <FilterRow
          filters={filterOptions}
          currentFilter={activeFilter}
          onFilterChange={handleFilterChange}
          isLoading={isPending}
          title={filterTitle}
          analyticsContext={{ filter_category: 'events' }}
        />
      )}

      {filteredEvents.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-grid-gutter">
            {displayedEvents.map((event: Event) => {
              const eventType = event.eventType as EventTypeData | undefined
              return (
                <Card
                  target={'_blank'}
                  type={CardType.Event}
                  key={event.id}
                  title={event.title}
                  startDate={event?.startDate ?? ''}
                  endDate={event?.endDate ?? ''}
                  location={event?.location ?? ''}
                  category={
                    eventType && typeof eventType === 'object'
                      ? {
                          title: eventType.title,
                          colorScheme: eventType.colorScheme,
                        }
                      : undefined
                  }
                  labels={event?.tags?.map((tag) => (tag as Tag)?.title)}
                  isRecommended={event.isRecommended ?? false}
                  recommendedBy={event.recommendedBy as AuthorData[]}
                  recommendedWhy={event.isRecommended ? event.recommendedWhy : undefined}
                  longRecommendation={event.isRecommended ? event.longRecommendation : undefined}
                  href={event.link?.link?.url}
                  image={event.image}
                  description={event.description}
                  organizer={event.organizedBy}
                  organizerIsLinkable={event.organizerIsLinkable}
                  organizerLinkType={event.organizerLinkType}
                  organizerVendor={event.organizerVendor}
                  organizerLink={event.organizerLink}
                  language={event.languages?.join(', ')}
                  link={event.link}
                  showCTAButton={event.showCTAButton}
                  secondLink={event.secondLink}
                  showSecondCTAButton={event.showSecondCTAButton}
                  locale={locale}
                />
              )
            })}
          </div>
          {!showAll && remainingEvents > 0 && (
            <div className="flex flex-col items-center gap-2 lg:pt-section pt-mobile-section">
              <p className="text-label-XS-regular text-text-faded">
                {remainingEvents} event{remainingEvents === 1 ? '' : 's'} remaining
              </p>
              <Button variant="secondary" size="md" onClick={() => setShowAll(true)}>
                Show more
              </Button>
            </div>
          )}
        </>
      ) : (
        <p className="text-gray-500">{emptyMessage}</p>
      )}
    </div>
  )
}
